import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Stack,
  InputAdornment,
  IconButton,
  Paper,
  Tooltip,
  MenuItem
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { StoreFormData, StoreLocation } from '../models/Store';
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api';
import { useAuth } from '../contexts/AuthContext';
import { storeService } from '../services/storeService';
import { sha256Hex } from '../utils/crypto';
import { OperationGroup } from '../models/OperationGroup';

// Define the map container styles
const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '4px',
};

// Initial map center (Casablanca, Morocco)
const defaultCenter = {
  lat: 33.5731104,
  lng: -7.5898434,
};

// Google Maps API key - In a real app, this should be in an environment variable
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

interface StoreDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (storeData: StoreFormData) => Promise<string>;
  operationGroups: OperationGroup[];
  store?: {
    id: string;
    name: string;
    address: string;
    location: StoreLocation;
    isActive: boolean;
    isDeliveryResponsible?: boolean;
    operationGroupId?: string | null;
    isVisibleOnline?: boolean;
    isPickupEnabled?: boolean;
    isDeliveryEnabled?: boolean;
    phone?: string;
    email?: string;
  };
  title: string;
}

// Helper for Geocoding and Reverse Geocoding
const useGeocoding = () => {
  const geocodeAddress = async (address: string): Promise<StoreLocation | null> => {
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ address });
      
      if (result.results && result.results.length > 0) {
        const location = result.results[0].geometry.location;
        return {
          latitude: location.lat(),
          longitude: location.lng()
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  const reverseGeocode = async (lat: number, lng: number): Promise<string | null> => {
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await geocoder.geocode({ location: { lat, lng } });
      
      if (result.results && result.results.length > 0) {
        return result.results[0].formatted_address;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  return { geocodeAddress, reverseGeocode };
};

const StoreDialog: React.FC<StoreDialogProps> = ({
  open,
  onClose,
  onSave,
  operationGroups,
  store,
  title,
}) => {
  // Google Maps loading state
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // Geocoding helpers
  const { geocodeAddress, reverseGeocode } = useGeocoding();
  const { currentUser } = useAuth();
  
  // Form state
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [addressSearch, setAddressSearch] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDeliveryResponsible, setIsDeliveryResponsible] = useState(false);
  const [operationGroupId, setOperationGroupId] = useState<string>('');
  const [isVisibleOnline, setIsVisibleOnline] = useState(false);
  const [isPickupEnabled, setIsPickupEnabled] = useState(true);
  const [isDeliveryEnabled, setIsDeliveryEnabled] = useState(true);
  const [location, setLocation] = useState<StoreLocation>({ latitude: 0, longitude: 0 });
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [codeReglages, setCodeReglages] = useState('');
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [geocodingStatus, setGeocodingStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Map reference
  const mapRef = useRef<google.maps.Map | null>(null);
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  // Initialize form with existing data if editing
  useEffect(() => {
    if (store) {
      setName(store.name);
      setAddress(store.address);
      setAddressSearch(store.address);
      setPhone(store.phone || '');
      setEmail(store.email || '');
      setIsActive(store.isActive);
      setIsDeliveryResponsible(store.isDeliveryResponsible || false);
      setOperationGroupId(store.operationGroupId || '');
      setIsVisibleOnline(store.isVisibleOnline === true);
      setIsPickupEnabled(store.isPickupEnabled !== false);
      setIsDeliveryEnabled(store.isDeliveryEnabled !== false);
      setLocation(store.location);
      setMapCenter({ lat: store.location.latitude, lng: store.location.longitude });
      setGeocodingStatus('success'); // When editing, we already have coordinates
    } else {
      // Reset form for new store
      setName('');
      setAddress('');
      setAddressSearch('');
      setPhone('');
      setEmail('');
      setIsActive(true);
      setIsDeliveryResponsible(false);
      setOperationGroupId(operationGroups[0]?.id || '');
      setIsVisibleOnline(false);
      setIsPickupEnabled(true);
      setIsDeliveryEnabled(true);
      setLocation({ latitude: 0, longitude: 0 });
      setMapCenter(defaultCenter);
      setGeocodingStatus('idle');
    }
    setCodeReglages('');
    setError(null);
  }, [store, open, operationGroups]);

  // Handle geocoding from address search
  const handleGeocode = async () => {
    if (!addressSearch.trim()) {
      setError('Veuillez saisir une adresse avant de géocoder');
      return;
    }

    setGeocodingStatus('loading');
    setError(null);

    try {
      const coords = await geocodeAddress(addressSearch);
      
      if (coords) {
        setLocation(coords);
        setAddress(addressSearch);
        setMapCenter({ lat: coords.latitude, lng: coords.longitude });
        setGeocodingStatus('success');
      } else {
        setGeocodingStatus('error');
        setError('Impossible de géocoder cette adresse');
      }
    } catch (err) {
      setGeocodingStatus('error');
      setError('Erreur lors du géocodage');
      console.error('Geocoding error:', err);
    }
  };

  // Handle click on map
  const handleMapClick = async (e: google.maps.MapMouseEvent) => {
    if (!e.latLng) return;
    
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    
    setGeocodingStatus('loading');
    setLocation({ latitude: lat, longitude: lng });
    
    try {
      const formattedAddress = await reverseGeocode(lat, lng);
      if (formattedAddress) {
        setAddress(formattedAddress);
        setAddressSearch(formattedAddress);
        setGeocodingStatus('success');
      } else {
        setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
        setAddressSearch('');
        setGeocodingStatus('success');
      }
    } catch (err) {
      console.error('Error reverse geocoding:', err);
      setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
      setGeocodingStatus('success');
    }
  };

  // Use current location
  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      setGeocodingStatus('loading');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          
          setLocation({ latitude: lat, longitude: lng });
          setMapCenter({ lat, lng });
          
          try {
            const formattedAddress = await reverseGeocode(lat, lng);
            if (formattedAddress) {
              setAddress(formattedAddress);
              setAddressSearch(formattedAddress);
              setGeocodingStatus('success');
            } else {
              setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
              setGeocodingStatus('success');
            }
          } catch (err) {
            console.error('Error reverse geocoding:', err);
            setAddress(`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`);
            setGeocodingStatus('success');
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Impossible d\'obtenir votre position actuelle');
          setGeocodingStatus('error');
        }
      );
    } else {
      setError('La géolocalisation n\'est pas supportée par votre navigateur');
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    if (!name.trim()) {
      setError('Le nom du magasin est requis');
      return;
    }

    if (!address.trim()) {
      setError('L\'adresse du magasin est requise');
      return;
    }

    if (!operationGroupId.trim()) {
      setError('Le groupe opérationnel est requis');
      return;
    }

    // Ensure we have coordinates (either from geocoding or pre-existing)
    if (location.latitude === 0 && location.longitude === 0) {
      setError('Veuillez géocoder l\'adresse pour obtenir les coordonnées');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Prepare store data
      const storeData: StoreFormData = {
        name,
        address,
        location,
        isActive,
        isDeliveryResponsible,
        operationGroupId: operationGroupId.trim(),
        isVisibleOnline,
        isPickupEnabled,
        isDeliveryEnabled,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined
      };

      const storeId = await onSave(storeData);

      // If a settings code was provided, compute and persist its hash
      if (codeReglages.trim()) {
        try {
          const hash = await sha256Hex(`${storeId}:${codeReglages.trim()}`);
          await storeService.setDupRulesCodeHash(storeId, hash, currentUser?.uid);
        } catch (e) {
          console.error('Erreur mise à jour "Code réglages":', e);
          // Keep saving the store; code update failure shouldn't crash the dialog
        }
      }

      onClose();
    } catch (err: any) {
      setError(`Erreur: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            {/* Name */}
            <TextField
              label="Nom du magasin"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              required
            />

            {/* Address Search with Geocoding */}
            <TextField
              label="Recherche d'adresse"
              value={addressSearch}
              onChange={(e) => setAddressSearch(e.target.value)}
              fullWidth
              required
              helperText="Saisissez une adresse et cliquez sur Rechercher, ou sélectionnez directement sur la carte"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleUseCurrentLocation}
                      edge="end"
                      title="Utiliser ma position"
                    >
                      <MyLocationIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleGeocode}
                      edge="end"
                      disabled={!addressSearch.trim() || geocodingStatus === 'loading'}
                    >
                      {geocodingStatus === 'loading' ? <CircularProgress size={24} /> : <SearchIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Google Maps Component */}
            <Paper elevation={2} sx={{ p: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Sélectionnez l'emplacement sur la carte (cliquez pour placer un marqueur)
              </Typography>
              {!isLoaded ? (
                <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  <CircularProgress />
                </Box>
              ) : loadError ? (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <strong>Erreur lors du chargement de Google Maps:</strong> 
                    <Box sx={{ mt: 1 }}>
                      La clé API Google Maps n'est pas correctement configurée ou l'API n'est pas activée.
                      <ul>
                        <li>Rendez-vous sur <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
                        <li>Créez un projet ou sélectionnez un projet existant</li>
                        <li>Activez l'API JavaScript Maps et l'API Geocoding</li>
                        <li>Créez une clé API et remplacez celle dans le code</li>
                      </ul>
                    </Box>
                  </Alert>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    En attendant, vous pouvez saisir manuellement les coordonnées GPS ci-dessous.
                  </Typography>
                  <Box 
                    sx={{ 
                      height: 200, 
                      bgcolor: 'action.hover', 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 1,
                      mb: 2
                    }}
                  >
                    <Typography color="text.secondary">
                      Le composant carte n'est pas disponible
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={15}
                  onClick={handleMapClick}
                  onLoad={onMapLoad}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: true,
                  }}
                >
                  {location.latitude !== 0 && location.longitude !== 0 && (
                    <Marker
                      position={{ lat: location.latitude, lng: location.longitude }}
                    />
                  )}
                </GoogleMap>
              )}
            </Paper>

            {/* Selected Address Display */}
            <TextField
              label="Adresse sélectionnée"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              fullWidth
              multiline
              rows={2}
              required
            />

            {/* Coordinates display */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Latitude"
                value={location.latitude}
                onChange={(e) => {
                  const lat = parseFloat(e.target.value) || 0;
                  setLocation({
                    ...location,
                    latitude: lat
                  });
                  setMapCenter({ ...mapCenter, lat });
                }}
                type="number"
                fullWidth
                disabled={geocodingStatus === 'loading'}
              />
              <TextField
                label="Longitude"
                value={location.longitude}
                onChange={(e) => {
                  const lng = parseFloat(e.target.value) || 0;
                  setLocation({
                    ...location,
                    longitude: lng
                  });
                  setMapCenter({ ...mapCenter, lng });
                }}
                type="number"
                fullWidth
                disabled={geocodingStatus === 'loading'}
              />
            </Box>

            {/* Contact Info */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Téléphone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                fullWidth
              />
              <TextField
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                type="email"
              />
            </Box>

            <TextField
              select
              label="Groupe opérationnel"
              value={operationGroupId}
              onChange={(e) => setOperationGroupId(e.target.value)}
              fullWidth
              required
              helperText="Les livreurs et tarifs de livraison sont partagés au niveau du groupe."
            >
              {operationGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name} ({group.id})
                </MenuItem>
              ))}
            </TextField>

            {/* Code réglages (admin) */}
            <TextField
              label="Code réglages"
              type="text"
              value={codeReglages}
              onChange={(e) => setCodeReglages(e.target.value)}
              fullWidth
              helperText={store ? "Laisser vide pour ne pas modifier le code existant" : "Optionnel. Si renseigné, protège les réglages du magasin"}
            />

            {/* Status Settings */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch 
                    checked={isActive} 
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                }
                label="Magasin actif"
              />
              
              <FormControlLabel
                control={
                  <Switch 
                    checked={isDeliveryResponsible} 
                    onChange={(e) => setIsDeliveryResponsible(e.target.checked)}
                    color="primary"
                    icon={<LocalShippingIcon />}
                    checkedIcon={<LocalShippingIcon />}
                  />
                }
                label={
                  <Tooltip title="Si activé, ce magasin devient le magasin responsable des livraisons pour son groupe opérationnel.">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <span>Responsable des livraisons</span>
                      <LocalShippingIcon fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
                    </Box>
                  </Tooltip>
                }
              />
              
              {isDeliveryResponsible && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Si vous définissez ce magasin comme responsable des livraisons, tout autre magasin responsable du même groupe perdra ce statut.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={isVisibleOnline}
                    onChange={(e) => setIsVisibleOnline(e.target.checked)}
                  />
                }
                label="Visible dans Step_Client"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isPickupEnabled}
                    onChange={(e) => setIsPickupEnabled(e.target.checked)}
                  />
                }
                label="Pickup activé"
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isDeliveryEnabled}
                    onChange={(e) => setIsDeliveryEnabled(e.target.checked)}
                  />
                }
                label="Livraison activée"
              />
            </Box>

            {/* Error display */}
            {error && (
              <Alert severity="error">{error}</Alert>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>Annuler</Button>
        <Button 
          onClick={handleSubmit} 
          color="primary" 
          variant="contained"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StoreDialog;
